/**
 * Wave 3 · B7 — bottom pill bar. A strip listing every open tab as a
 * pill: click to activate, pin to protect from auto-close, move left/right
 * to reorder (drag would be nicer but button-reorder is the explicitly-
 * allowed "simple is fine" fallback), × to close. Hidden entirely when
 * there's only one tab open (nothing to switch between yet — avoids
 * permanent chrome for the common single-record session).
 *
 * UI overhaul PR-1a: the bar sits in the shell's normal flow (the last row of
 * the `h-screen` column in App.tsx) instead of `position: fixed` over the
 * content. A fixed bar needed a 40px spacer inside `<main>` — which gave every
 * page a phantom scrollbar (audit APP-04) — and still covered the sidebar's
 * last item (APP-06). In flow, the sidebar and the content both end where the
 * bar begins, and nothing needs reserving.
 */
import { Pin, PinOff, X, ChevronLeft, ChevronRight, Info } from 'lucide-react'
import { useTabs } from './TabsContext'

const BAR_HEIGHT = 40

export default function TabBar() {
  const { tabs, activeKey, activate, close, togglePin, move, notice, dismissNotice } = useTabs()

  if (tabs.length <= 1) return null

  return (
    <div
      className="relative flex w-full shrink-0 items-stretch overflow-x-auto border-t border-edge bg-surface"
      style={{ height: BAR_HEIGHT }}
      role="tablist"
      aria-label="Open tabs"
    >
      {notice && (
        <div
          role="status"
          className="fixed bottom-11 right-3 z-40 flex items-center gap-2 rounded-md border border-edge bg-surface px-3 py-2 text-xs text-ink shadow-lg max-w-xs"
        >
          <Info size={13} className="shrink-0 text-ink-faint" />
          <span className="flex-1">{notice}</span>
          <button
            onClick={dismissNotice}
            className="text-ink-faint hover:text-ink shrink-0"
            aria-label="Dismiss"
          >
            <X size={12} />
          </button>
        </div>
      )}
      {tabs.map((tab, i) => {
        const isActive = tab.id === activeKey
        return (
          <div
            key={tab.id}
            role="tab"
            aria-selected={isActive}
            className={`group flex items-center gap-1 pl-2 pr-1 border-r border-edge shrink-0 max-w-[14rem] cursor-pointer select-none ${
              isActive ? 'bg-canvas text-ink' : 'text-ink-faint hover:bg-canvas/60'
            }`}
            onClick={() => activate(tab.id)}
            title={tab.title || tab.location.pathname}
          >
            {tab.pinned && <Pin size={11} className="shrink-0 text-accent" aria-hidden />}
            <span className="truncate text-xs py-0 leading-none">{tab.title || tab.location.pathname}</span>

            <button
              onClick={(e) => { e.stopPropagation(); move(tab.id, -1) }}
              disabled={i === 0}
              className="shrink-0 p-0.5 opacity-0 group-hover:opacity-100 disabled:opacity-0 hover:text-ink"
              aria-label={`Move ${tab.title} left`}
              title="Move left"
            >
              <ChevronLeft size={11} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); move(tab.id, 1) }}
              disabled={i === tabs.length - 1}
              className="shrink-0 p-0.5 opacity-0 group-hover:opacity-100 disabled:opacity-0 hover:text-ink"
              aria-label={`Move ${tab.title} right`}
              title="Move right"
            >
              <ChevronRight size={11} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); togglePin(tab.id) }}
              className="shrink-0 p-0.5 opacity-0 group-hover:opacity-100 hover:text-ink"
              aria-label={tab.pinned ? `Unpin ${tab.title}` : `Pin ${tab.title}`}
              title={tab.pinned ? 'Unpin' : 'Pin — protects this tab from auto-close'}
            >
              {tab.pinned ? <PinOff size={11} /> : <Pin size={11} />}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); close(tab.id) }}
              className="shrink-0 p-0.5 opacity-0 group-hover:opacity-100 hover:text-danger"
              aria-label={`Close ${tab.title}`}
              title="Close"
            >
              <X size={12} />
            </button>
          </div>
        )
      })}
    </div>
  )
}
